import { useMemo, useState } from 'react';
import { RefreshCw, Filter, Eye, Siren, X } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { getMapHandle } from '@/hooks/mapHandleRegistry';
import {
  ALL_MISSION_CATEGORIES,
  DEFAULT_FILTERS,
  type MissionFilters,
} from './missionsTabFilters';
import type { Mission, MissionCategory, MissionDifficulty, MissionStatus } from '@/state/types';

const STATUS_LABEL: Record<MissionStatus, string> = {
  open: 'Offen',
  dispatched: 'Maßnahmen eingeleitet',
  onscene: 'Vor Ort',
  closed: 'Abgeschlossen',
  failed: 'Fehlgeschlagen',
};

const STATUS_PILL: Record<MissionStatus, string> = {
  open: 'bg-accent-dispatch/15 text-accent-dispatch',
  dispatched: 'bg-accent-dispatch/15 text-accent-dispatch',
  onscene: 'bg-amber-500/15 text-amber-400',
  closed: 'bg-status-available/15 text-status-available',
  failed: 'bg-accent-emergency/15 text-accent-emergency',
};

const CAT_LABEL: Record<MissionCategory, string> = {
  fire: 'Brand',
  rescue: 'Rettung',
  technical: 'Technisch',
  police: 'Polizei',
};

const DIFF_LABEL: Record<MissionDifficulty, string> = {
  easy: 'Leicht',
  medium: 'Mittel',
  hard: 'Schwer',
  expert: 'Experte',
  extreme: 'Extrem',
};


export function MissionsTab() {
  const missions = useGameStore((s) => s.missions);
  const selectDetail = useGameStore((s) => s.selectDetail);
  const [filters, setFilters] = useState<MissionFilters>(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  const filtered = useMemo(() => {
    return missions.filter((m) => {
      const isActive = m.status === 'open' || m.status === 'dispatched' || m.status === 'onscene';
      const statusBucket: 'active' | 'closed' | 'failed' = isActive
        ? 'active'
        : m.status === 'closed'
          ? 'closed'
          : 'failed';
      if (!filters.statuses.has(statusBucket)) return false;
      if (!filters.categories.has(m.category)) return false;
      if (!filters.difficulties.has(m.difficulty)) return false;
      return true;
    });
  }, [missions, filters]);

  return (
    <div className="flex h-full flex-col">
      <TabHeader
        title={`Einsätze (${filtered.length})`}
        onFilter={() => setFilterOpen((o) => !o)}
      />

      {filterOpen && (
        <FilterPopover
          filters={filters}
          onChange={setFilters}
          onClose={() => setFilterOpen(false)}
          onReset={() => setFilters(DEFAULT_FILTERS)}
        />
      )}

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="space-y-3">
            {filtered.map((m) => (
              <MissionCard
                key={m.id}
                mission={m}
                onClick={() => selectDetail({ kind: 'mission', id: m.id })}
                onFocus={() => {
                  const h = getMapHandle();
                  if (h) h.flyTo(m.position, 16);
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface MissionCardProps {
  mission: Mission;
  onClick: () => void;
  onFocus: () => void;
}

function MissionCard({ mission, onClick, onFocus }: MissionCardProps) {
  return (
    <li className="overflow-hidden rounded-lg border border-border-subtle bg-bg-elevated/40 transition hover:border-border-strong">
      <button
        type="button"
        onClick={onClick}
        className="block w-full p-4 text-left transition hover:bg-bg-elevated/60"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-base font-bold">{mission.title}</p>
            <p className="mt-0.5 text-xs text-fg-muted">
              {mission.code} · {CAT_LABEL[mission.category]} · {DIFF_LABEL[mission.difficulty]}
            </p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <span
              className={[
                'rounded-full px-2.5 py-1 text-[10px] font-semibold',
                STATUS_PILL[mission.status],
              ].join(' ')}
            >
              {STATUS_LABEL[mission.status]}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onFocus();
              }}
              aria-label="Auf Karte zentrieren"
              className="flex h-7 w-7 items-center justify-center rounded-md text-fg-muted transition hover:bg-bg-elevated hover:text-fg-primary"
            >
              <Eye className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </div>

        <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-bg-primary">
          <div
            className="h-full rounded-full bg-accent-emergency transition-all"
            style={{ width: `${Math.round(mission.progress * 100)}%` }}
          />
        </div>
      </button>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-12 text-center">
      <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-border-strong text-fg-muted">
        <Siren className="h-5 w-5" aria-hidden />
      </span>
      <p className="font-display text-base font-semibold text-fg-secondary">
        Keine Einsätze passen zum Filter
      </p>
      <p className="mt-1 max-w-xs text-xs text-fg-muted">
        Notrufe spawnen, sobald mindestens eine Wache fertig ist und der Mode-spezifische
        Intervall greift. Filter zurücksetzen, um alle zu sehen.
      </p>
    </div>
  );
}

interface TabHeaderProps {
  title: string;
  onFilter: () => void;
}

export function TabHeader({ title, onFilter }: TabHeaderProps) {
  return (
    <header className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
      <h2 className="font-display text-base font-bold">{title}</h2>
      <div className="flex items-center gap-1">
        <IconBtn label="Filter" icon={Filter} onClick={onFilter} />
      </div>
    </header>
  );
}

function IconBtn({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof RefreshCw;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition hover:bg-bg-elevated hover:text-fg-primary"
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

interface FilterPopoverProps {
  filters: MissionFilters;
  onChange: (next: MissionFilters) => void;
  onClose: () => void;
  onReset: () => void;
}

function FilterPopover({ filters, onChange, onClose, onReset }: FilterPopoverProps) {
  const toggle =
    <K extends keyof MissionFilters>(group: K, value: MissionFilters[K] extends Set<infer V> ? V : never) =>
    () => {
      const cur = filters[group] as Set<unknown>;
      const next = new Set(cur);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      onChange({ ...filters, [group]: next } as MissionFilters);
    };

  return (
    <div className="border-b border-border-subtle bg-bg-elevated/40 px-5 py-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-fg-muted">Filter</p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onReset}
            className="text-[10px] uppercase tracking-wider text-fg-muted hover:text-fg-primary"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Filter schließen"
            className="ml-2 flex h-6 w-6 items-center justify-center rounded text-fg-muted hover:text-fg-primary"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </div>
      </div>
      <FilterGroup
        label="Status"
        options={[
          { id: 'active', label: 'Aktiv' },
          { id: 'closed', label: 'Erfolgreich' },
          { id: 'failed', label: 'Fehlgeschlagen' },
        ]}
        active={filters.statuses}
        onToggle={(v) => toggle('statuses', v as 'active' | 'closed' | 'failed')()}
      />
      <FilterGroup
        label="Kategorie"
        options={ALL_MISSION_CATEGORIES.map((c) => ({
          id: c,
          label: CAT_LABEL[c],
        }))}
        active={filters.categories}
        onToggle={(v) => toggle('categories', v as MissionCategory)()}
      />
      <FilterGroup
        label="Schwierigkeit"
        options={(['easy', 'medium', 'hard', 'expert', 'extreme'] as MissionDifficulty[]).map(
          (d) => ({ id: d, label: DIFF_LABEL[d] }),
        )}
        active={filters.difficulties}
        onToggle={(v) => toggle('difficulties', v as MissionDifficulty)()}
      />
    </div>
  );
}

function FilterGroup({
  label,
  options,
  active,
  onToggle,
}: {
  label: string;
  options: { id: string; label: string }[];
  active: Set<string>;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="mt-2">
      <p className="mb-1 text-[10px] text-fg-muted">{label}</p>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const on = active.has(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onToggle(o.id)}
              className={[
                'rounded-full px-2.5 py-1 text-[10px] font-semibold transition',
                on
                  ? 'bg-accent-dispatch text-white'
                  : 'border border-border-subtle bg-bg-primary text-fg-muted hover:text-fg-primary',
              ].join(' ')}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
